import { create } from "zustand"
import { type PersistOptions, persist } from "zustand/middleware"
import { defaultPreferences } from "@/config/defaultPreferences"
import type { Preferences } from "./storeState"

// Định nghĩa kiểu cho category và key
export type Category = keyof Preferences
export type SettingKey<T extends Category> = keyof Preferences[T]

/**
 * Lấy giá trị trong preferences
 * @param preferences - Đối tượng preferences trong kho lưu trữ trạng thái toàn cục
 * @param category - Danh mục (Category)
 * @param key - Khóa thiết lập (Setting Key)
 * @returns Giá trị thiết lập
 */
const getPreferenceValue = <T extends Category, K extends SettingKey<T>>(
  preferences: Preferences,
  category: T,
  pKey: K
): Preferences[T][K] => {
  return preferences[category][pKey]
}

interface PreferencesStore {
  // Trạng thái cấu hình hệ thống
  preferences: Preferences
  // Cập nhật trạng thái toàn cục
  updatePreferences: (category: Category, key: string, value: any) => void
  // Đặt lại trạng thái toàn cục (Reset)
  resetPreferences: () => void
}

// Tạo store cấu hình toàn cục (Preferences Store)
const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set) => ({
      // Trạng thái cấu hình hệ thống
      preferences: defaultPreferences,
      // Cập nhật trạng thái toàn cục
      updatePreferences: (category: Category, key: string, value: any) =>
        set((state) => ({
          preferences: {
            ...state.preferences,
            [category]: {
              ...state.preferences[category],
              [key]: value,
            },
          },
        })),
      // Đặt lại trạng thái toàn cục
      resetPreferences: () => set({ preferences: defaultPreferences }),
    }),
    {
      name: "preferences",
      getStorage: () => localStorage,
    } as PersistOptions<PreferencesStore>
  )
)

export { getPreferenceValue, usePreferencesStore }
